class ProductEmbed extends HTMLElement {
  static cache = new Map();

  connectedCallback() {
    this.handle = this.dataset.handle;
    if (!this.handle) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.observer.disconnect();
            this.load();
          }
        });
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  async load() {
    this.classList.add('vp-embed--loading');
    try {
      const product = await ProductEmbed.fetchProduct(this.handle);
      if (!product) {
        this.renderError();
        return;
      }
      this.render(product);
    } catch (error) {
      this.renderError();
    }
  }

  static async fetchProduct(handle) {
    if (ProductEmbed.cache.has(handle)) {
      return ProductEmbed.cache.get(handle);
    }

    const sessionKey = `vp-product-${handle}`;
    try {
      const stored = sessionStorage.getItem(sessionKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        ProductEmbed.cache.set(handle, parsed);
        return parsed;
      }
    } catch (e) {
      // sessionStorage unavailable, fall through to fetch
    }

    const response = await fetch(`/products/${handle}.json`);
    if (!response.ok) {
      ProductEmbed.cache.set(handle, null);
      return null;
    }

    const data = await response.json();
    const product = data.product || null;
    ProductEmbed.cache.set(handle, product);

    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(product));
    } catch (e) {
      // storage full or unavailable, skip caching
    }

    return product;
  }

  formatMoney(price) {
    const currency = document.documentElement.dataset.vpCurrency || 'AUD';
    const amount = parseFloat(price);
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `$${amount.toFixed(2)}`;
    }
  }

  imageUrl(src, width) {
    try {
      const url = new URL(src);
      const parts = url.pathname.split('.');
      const ext = parts.pop();
      url.pathname = `${parts.join('.')}_${width}x.${ext}`;
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  escapeHtml(str = '') {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  render(product) {
    const variant = (product.variants && product.variants[0]) || null;
    if (!variant) {
      this.renderError();
      return;
    }

    const image = product.images && product.images[0];
    const price = this.formatMoney(variant.price);
    const hasCompare = variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price);
    const comparePrice = hasCompare ? this.formatMoney(variant.compare_at_price) : null;
    const url = `/products/${product.handle}`;

    this.classList.remove('vp-embed--loading');
    this.classList.add('vp-embed--loaded');

    this.innerHTML = `
      <a href="${url}" class="vp-embed__link">
        <span class="vp-embed__image-wrap">
          ${image ? `<img src="${this.imageUrl(image.src, 600)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="600" height="600" class="vp-embed__image">` : ''}
        </span>
        <span class="vp-embed__details">
          <span class="vp-embed__vendor">${this.escapeHtml(product.vendor)}</span>
          <span class="vp-embed__title">${this.escapeHtml(product.title)}</span>
          <span class="vp-embed__price">
            ${comparePrice ? `<span class="vp-embed__price--compare">${comparePrice}</span>` : ''}
            <span class="${comparePrice ? 'vp-embed__price--sale' : ''}">${price}</span>
          </span>
        </span>
      </a>
    `;
  }

  renderError() {
    this.classList.remove('vp-embed--loading');
    this.classList.add('vp-embed--error');
    this.innerHTML = '';
  }
}

customElements.define('product-embed', ProductEmbed);